import { ReactElement, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../../../configuration/configuration";
import { comparisonType, IEventCondition, IEventVariable, Severity } from "../../../types";
import StyledTableRow from "../../../../../components/styledTableRow";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { IAcquisitionVariableFullData } from "../../../../../types";
import { getUID } from "../../../../../system/utils";

const maxVar = 6;

interface IProps {
  data: IEventVariable;
  machineVariables: IAcquisitionVariableFullData[];
  onCancel: () => void;
  onAccept: (conditions: IEventCondition[]) => void;
}

interface IVariableRowProps {
  index: number;
  machineVariables: IAcquisitionVariableFullData[];
  data: IEventCondition;
  setData: (data: IEventCondition) => void;
  onRemoveVariable: (index: number) => void;
}


const VariableRow = ({ index, machineVariables, data, setData, onRemoveVariable }: IVariableRowProps): ReactElement => {
  const found = data.type === "TAG" ? machineVariables.find(x => x.code === data.value) ?? null : null;

  const updateItemsSeries = (field: keyof IEventCondition, value: any) => {
    if (field === "title") {
      data[field] = value;
    } else if(field === "comparison") {
      data[field] = value;
    } else if(field === "value") {
      data[field] = value;
    } else if(field === "severity") {
      data[field] = value;
    } else if(field === "type") {
      data[field] = value;
    }

    setData(data);
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <TextField
            variant="standard"
            size="small"
            value={data.title}
            type="text"
            onChange={(event) => updateItemsSeries("title",  event.target.value as string)}
          />
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.comparison}
            displayEmpty={true}
            onChange={(event) => updateItemsSeries("comparison", event.target.value as comparisonType)}>
            <MenuItem value={">"}>&gt;</MenuItem>
            <MenuItem value={">="}>&gt;=</MenuItem>
            <MenuItem value={"<"}>&lt;</MenuItem>
            <MenuItem value={"<="}>&lt;=</MenuItem>
            <MenuItem value={"<>"}>&lt;&gt;</MenuItem>
            <MenuItem value={"="}>=</MenuItem>
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <Select
          sx={{ width: 100 }}
          variant="standard"
          value={data?.type ?? "VALUE"}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) => updateItemsSeries("type", event.target.value as string)}>
          <MenuItem value={"TAG"}>TAG</MenuItem>
          <MenuItem value={"VALUE"}>VALUE</MenuItem>
        </Select>
      </TableCell>

      <TableCell>
        {data.type === "TAG" &&
          <Autocomplete
            sx={{ width: 300 }}
            options={machineVariables}
            getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
            value={found}
            openOnFocus={true}
            onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
              updateItemsSeries("value", newValue?.code);
            }}
            renderInput={(params) =>
              <TextField {...params} variant="standard" color={"primary"}/>
            }
          />
        }

        {data.type === "VALUE" &&
          <TextField
            sx={{ width: 300 }}
            variant="standard"
            size="small"
            value={(data.value ?? 0) as number}
            type="number"
            onChange={(event) => updateItemsSeries("value", parseFloat(event.target.value as string))}
          />
        }
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={`${data.severity}`}
            displayEmpty={true}
            onChange={(event: SelectChangeEvent) => updateItemsSeries("severity", parseInt(event.target.value, 10))}>
            <MenuItem value={`${Severity.Low}`}>Low</MenuItem>
            <MenuItem value={`${Severity.Medium}`}>Medium</MenuItem>
            <MenuItem value={`${Severity.High}`}>High</MenuItem>
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <IconButton sx={{ padding: 0, margin: 0 }} onClick={() => onRemoveVariable(index)}>
          <Tooltip title={"Remove variable"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

    </StyledTableRow>
  );
};

const ConditionEditor = ({ onAccept, onCancel, data, machineVariables }: IProps): ReactElement => {
  const [conditions, setConditions] = useState<IEventCondition[]>(data.conditions ?? []);

  const addCondition = () => {
    setConditions([ ...conditions,  {
      hash: getUID(),
      title: "",
      type: "VALUE",
      value: 0,
      comparison: "=",
      severity: Severity.Low
    }]);
  };

  const onSetData = () => {
    setConditions([ ...conditions ]);
  };

  const onRemoveVariable = (index: number) => {
    conditions.splice(index, 1);
    setConditions({ ...conditions });
  };

  const onInternalAccept = () => {
    onAccept(conditions);
  };

  return (
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={true} maxWidth="lg">
      <DialogTitle id="customized-dialog-title">
        Conditions
      </DialogTitle>

      <DialogContent dividers={true}>
        <Table stickyHeader={true} size={"small"} >
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
                <IconButton sx={{ padding: 0, margin: 0 }} style={{ maxWidth: 21 }} onClick={addCondition} disabled={conditions.length >= maxVar || machineVariables.length === 0}>
                  <Tooltip title={"Add variable"}>
                    <Add style={{ color: getPalette().statusColor.info.background }}/>
                  </Tooltip>
                </IconButton>
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 200 }}>Title</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Condition</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Type</TableCell>
              <TableCell className="table-header" style={{ minWidth: 200 }}>Value</TableCell>
              <TableCell className="table-header" >Status</TableCell>
              <TableCell className="table-header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.map((condition, index) => {

              return(
                <VariableRow key={`event-condition-${index}`}
                  index={index} machineVariables={machineVariables}
                  data={condition} setData={onSetData} onRemoveVariable={onRemoveVariable}/>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button onClick={onInternalAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConditionEditor;
